import api from './api';
import { LoginResponse, User } from '../types';

/**
 * Authentication service for Laravel backend integration
 * Handles Firebase ID token verification and user synchronization
 */
class AuthService {
  /**
   * Login user by sending Firebase ID token to Laravel backend
   * This verifies the token and creates/updates user in the database
   *
   * @param idToken - Firebase ID token from authenticated user
   * @returns Response with user data from Laravel
   */
  async login(idToken: string): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', { idToken });
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user from Laravel backend
   *
   * @param idToken - Firebase ID token
   * @returns User data with database info
   */
  async getCurrentUser(idToken: string): Promise<User> {
    try {
      const response = await api.get<User>('/auth/me', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      return response;
    } catch (error) {
      console.error('Failed to get user:', error);
      throw error;
    }
  }

  /**
   * Logout user (notify backend)
   * Client should discard Firebase token after this
   *
   * @param idToken - Firebase ID token
   * @returns Logout response
   */
  async logout(idToken: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post<{ success: boolean; message: string }>('/auth/logout', null, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      return response;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;
